class CartFreeProductChecker extends HTMLElement {
  constructor() {
    super();
    this.checkFreeProduct = this.checkFreeProduct.bind(this);
    this.handleCartDrawerVisibility = this.handleCartDrawerVisibility.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.debouncedCheck = this.debounce(this.fetchCartAndCheck.bind(this), 150);
    this.DEBUG = false;
    this.isInitialized = false;
  }

  log(...args) {
    if (this.DEBUG) {
      console.log('[FreeProductChecker]', ...args);
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  connectedCallback() {
    // Warten bis das DOM geladen ist
    requestAnimationFrame(() => {
      this.cartDrawer = document.getElementById('cart-drawer');
      if (this.cartDrawer) {
        this.freeProductDiv = this.querySelector('.ds-cart-drawer__free-product');
        this.freeProductHeader = this.cartDrawer.querySelector('.ds-free-products-header');
        this.freeProductId = this.getAttribute('data-free-product-id');
        // Convert threshold to cents to match cart.total_price
        this.freeProductThreshold = parseFloat(this.getAttribute('data-free-product-threshold')) * 100 || 0;

        this.log('Initialized with:', {
          freeProductId: this.freeProductId,
          threshold: this.freeProductThreshold,
          thresholdEuro: this.freeProductThreshold / 100
        });

        this.setupEventListeners();
        this.isInitialized = true;

        // Initial check - always run when component connects
        this.log('Running initial cart check...');
        setTimeout(() => {
          this.debouncedCheck();
        }, 100);

        // Additional check if cart drawer is open
        if (this.cartDrawer.classList.contains('is-open')) {
          this.log('Cart drawer is already open, running additional check');
          setTimeout(() => {
            this.debouncedCheck();
          }, 200);
        }
      }
    });
  }

  setupEventListeners() {
    // Listen for standard cart events
    document.addEventListener('cart:change', this.checkFreeProduct);
    document.addEventListener('cart:updated', this.checkFreeProduct);
    document.addEventListener('variant:add', this.debouncedCheck);

    // Set up mutation observer for cart drawer visibility
    this.observer = new MutationObserver(this.handleCartDrawerVisibility);
    this.observer.observe(this.cartDrawer, {
      attributes: true,
      attributeFilter: ['class']
    });

    // CRITICAL: Listen for quantity input changes in cart drawer
    this.cartDrawer.addEventListener('input', this.handleQuantityChange);
    this.cartDrawer.addEventListener('change', this.handleQuantityChange);

    // Listen for line item updates specifically
    this.cartDrawer.addEventListener('line-item:change', this.debouncedCheck);
    this.cartDrawer.addEventListener('line-item:remove', this.debouncedCheck);

    this.log('Event listeners setup complete');
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.checkFreeProduct);
    document.removeEventListener('cart:updated', this.checkFreeProduct);
    document.removeEventListener('variant:add', this.debouncedCheck);

    if (this.cartDrawer) {
      this.cartDrawer.removeEventListener('input', this.handleQuantityChange);
      this.cartDrawer.removeEventListener('change', this.handleQuantityChange);
      this.cartDrawer.removeEventListener('line-item:change', this.debouncedCheck);
      this.cartDrawer.removeEventListener('line-item:remove', this.debouncedCheck);
    }

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  handleQuantityChange(event) {
    const target = event.target;

    // Check for any quantity input in cart drawer
    if (target.matches('.quantity-input') ||
        target.matches('input[is="quantity-input"]') ||
        target.matches('input[data-line-key]')) {

      this.log('Quantity input detected:', {
        value: target.value,
        lineKey: target.dataset.lineKey,
        className: target.className
      });

      // Immediate debounced check
      this.debouncedCheck();
    }
  }

  handleCartDrawerVisibility(mutations) {
    for (const mutation of mutations) {
      if (mutation.target.classList.contains('is-open')) {
        this.log('Cart drawer opened, fetching fresh cart data');
        // Small delay to ensure cart is fully rendered
        setTimeout(() => {
          this.debouncedCheck();
        }, 50);
        break;
      }
    }
  }

  async fetchCartAndCheck() {
    if (!this.isInitialized) return;

    try {
      this.log('Fetching cart data...');
      const response = await fetch('/cart.js');
      const cartData = await response.json();

      this.log('Fresh cart data:', {
        total_price: cartData.total_price,
        total_price_euro: cartData.total_price / 100,
        items_count: cartData.items.length,
        items: cartData.items.map(item => ({
          id: item.product_id,
          quantity: item.quantity,
          title: item.product_title
        }))
      });

      this.checkFreeProduct({
        detail: {
          cart: cartData
        }
      });
    } catch (error) {
      this.log('Error fetching cart:', error);
    }
  }

  checkFreeProduct(event) {
    if (!this.freeProductDiv || !this.freeProductId || !this.isInitialized) {
      this.log('Missing elements, product ID, or not initialized');
      return;
    }

    const cartItems = event.detail?.cart?.items || [];
    const cartTotal = event.detail?.cart?.total_price || 0;

    this.log('Checking free product:', {
      productId: this.freeProductId,
      cartTotal: cartTotal,
      cartTotalEuro: cartTotal / 100,
      threshold: this.freeProductThreshold,
      thresholdEuro: this.freeProductThreshold / 100,
      itemsCount: cartItems.length
    });

    const freeProductInCart = cartItems.some(item =>
      String(item.product_id) === String(this.freeProductId)
    );

    const shouldShowFreeProduct = !freeProductInCart && cartTotal >= this.freeProductThreshold;

    this.log('Decision:', {
      freeProductInCart,
      shouldShowFreeProduct,
      currentDisplay: this.freeProductDiv.style.display
    });

    // Update visibility based on cart value and product presence
    const newDisplay = shouldShowFreeProduct ? 'block' : 'none';
    if (this.style.display !== newDisplay) {
      this.log('Changing component visibility from', this.style.display, 'to', newDisplay);
      this.style.display = newDisplay;
    }

    // Also update the inner product div
    if (this.freeProductDiv) {
      const innerDisplay = shouldShowFreeProduct ? 'grid' : 'none';
      this.freeProductDiv.style.display = innerDisplay;
    }

    // Schedule header visibility check after all instances have updated
    clearTimeout(this.headerCheckTimeout);
    this.headerCheckTimeout = setTimeout(() => {
      this.updateHeaderVisibility();
    }, 10);
  }

  updateHeaderVisibility() {
    if (!this.freeProductHeader) {
      this.log('No header element found!');
      return;
    }

    const allFreeProductComponents = this.cartDrawer.querySelectorAll('cart-free-product-checker');
    const visibleComponents = Array.from(allFreeProductComponents).filter(component =>
      component.style.display !== 'none' && component.style.display !== ''
    );

    const anyFreeProductVisible = visibleComponents.length > 0;

    this.log('Header visibility check:', {
      totalComponents: allFreeProductComponents.length,
      visibleComponents: visibleComponents.length,
      shouldShowHeader: anyFreeProductVisible,
      currentHeaderDisplay: this.freeProductHeader.style.display
    });

    const newHeaderDisplay = anyFreeProductVisible ? 'flex' : 'none';
    if (this.freeProductHeader.style.display !== newHeaderDisplay) {
      this.log('Updating header visibility from', this.freeProductHeader.style.display, 'to', newHeaderDisplay);
      this.freeProductHeader.style.display = newHeaderDisplay;
    } else {
      this.log('Header display unchanged:', newHeaderDisplay);
    }
  }
}

customElements.define('cart-free-product-checker', CartFreeProductChecker);